// Lightweight client-side helper to send events to our server proxy.

import { getClientConsent } from "@/lib/analytics/consent";
import type { Events } from "@/lib/analytics/events";

const IS_DEV = process.env.NODE_ENV === "development";

function buildUrl(path: string): string {
  const base = (process.env.NEXT_PUBLIC_BASE_PATH || "").replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}` || p;
}

/**
 * Ask the server for a `dpid` cookie. Middleware mints one on a rendered
 * response, but the detail pages are served from a shared cache whose hits
 * never reach middleware, so the consent banner asks for it directly.
 *
 * Returns a promise so callers can wait for the mint before firing analytics
 * that also call `getOrSetDistinctId` (otherwise two concurrent first mints
 * can disagree).
 */
export function requestDistinctId(): Promise<void> {
  try {
    return fetch(buildUrl("/api/analytics/dpid"), {
      method: "POST",
      keepalive: true,
    }).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    // Sync throw before the request starts (no window / aborted).
    return Promise.resolve();
  }
}

export function track<EventType extends keyof Events>(
  event: EventType,
  properties?: Events[EventType],
): void {
  if (!event) return;
  if (getClientConsent() !== "accepted") return;

  const url = buildUrl("/api/analytics/track");
  const payload = JSON.stringify({ event, properties });

  if (IS_DEV) {
    void import("@/lib/logger").then(({ logger }) =>
      logger.info({
        msg: "[analytics][dry-run] client track",
        event,
        properties,
      }),
    );
  }

  if (navigator?.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    } catch {
      // ignore and fall back
    }
  }

  // Fetch fallback with keepalive for unload scenarios
  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch {
    // ignore
  }
}
